import { Stack } from "@mui/material";
import { Card, Typography } from "@platform/design-system";

import { checklistFieldsByLine } from "../checklist";
import { FIELD_LABELS } from "../labels";

import type { CensusLineCheck } from "../hooks/useCensusValidation";

interface CensusRulesCardProps {
  readonly lines: readonly CensusLineCheck[];
}

/**
 * What every census is checked against, for the lines this RFP quotes.
 *
 * Stated plainly and without reference to this particular census: an analyst
 * seeing a field reported missing can read what was expected and why, rather
 * than inferring the rule from the failure.
 */
export const CensusRulesCard = ({
  lines,
}: CensusRulesCardProps): React.JSX.Element | null => {
  const rules = checklistFieldsByLine(lines);

  if (rules.length === 0) {
    return null;
  }

  return (
    <Card sx={{ p: 2 }} data-testid="census-rules">
      <Stack gap={2}>
        <Stack gap={0.5}>
          <Typography variant="subtitle1">Validation rules</Typography>
          <Typography variant="caption" color="textSecondary">
            Every census is checked against these, for the coverages this RFP
            quotes.
          </Typography>
        </Stack>

        {rules.map(({ line, fields }) => (
          <Stack gap={0.5} key={line}>
            <Typography variant="subtitle2">{line}</Typography>
            <Typography variant="body-medium" color="textSecondary">
              {fields.map((field) => FIELD_LABELS[field]).join(" · ")}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Card>
  );
};
